import { SmartRenderer } from '@fusioncharts/core/src/component-interface';
import nodeFactory from '../../factories/chord-node-factory';
import linkFactory from '../../factories/chord-link-factory';
import { convertColor, toPrecision, pluckNumber } from '@fusioncharts/core/src/lib';

const comparator = (a, b) => a - b;

class NodeLinkManager extends SmartRenderer {
  constructor (id) {
    super(id);
    this.registerFactory('chord-node-factory', nodeFactory);
    this.registerFactory('chord-link-factory', linkFactory);
  }

  __setDefaultConfig () {
    super.__setDefaultConfig();
    let config = this.config;
    config.total = 0;
    config.adjustedTotal = 0;
  }

  configureAttributes (dataObj) {
    super.configureAttributes(dataObj);
    Object.assign(this.config, dataObj);
    let manager = this,
      config = manager.config,
      total,
      { totalAngle, startingAngle, nodesOrder, nodes, linksOrder, links } = config;

    totalAngle -= (config.nodeSpacing * nodesOrder.length);

    total = config.total = nodesOrder.reduce((acc, label) => acc + nodes[label].total, 0);

    config.adjustedTotal = nodesOrder.reduce((acc, label) => {
      let node = nodes[label];
      if (node.total / (total || 1) < config.minNodeSize) {
        node.adjustedTotal = (total || 1) * config.minNodeSize;
      } else {
        node.adjustedTotal = node.total;
      }
      return acc + node.adjustedTotal;
    }, 0);

    config.interactiveLegend = pluckNumber(manager.getFromEnv('chart-attrib').interactivelegend, 1);

    const angleRatio = totalAngle / config.adjustedTotal;

    nodesOrder.forEach(label => {
      let node = nodes[label];
      node.startingAngle = startingAngle;
      node.arcAngle = node.adjustedTotal * angleRatio;
      node.endingAngle = startingAngle + node.arcAngle;
      node.scale.setRange([node.startingAngle, node.endingAngle])
        .setDomain([0, node.adjustedTotal]);
      startingAngle = node.endingAngle + config.nodeSpacing;
    });

    linksOrder.forEach(key => {
      if (links[key].visible) {
        let link = links[key],
          from = config.isPost ? link.linkedNodes[1] : link.linkedNodes[0],
          to = config.isPost ? link.linkedNodes[0] : link.linkedNodes[1],
          firstNode = nodes[from],
          secondNode = nodes[to],
          firstScale = firstNode.scale,
          secondScale = secondNode.scale,
          p1 = firstScale.getRangeValue(firstNode.nodeCovered),
          p2 = firstScale.getRangeValue(firstNode.nodeCovered += (link[from] || 0)),
          p3,
          p4;
        if (link.points.length) {
          link.points.length = 0;
        }
        if (from === to) {
          p3 = p1;
          p4 = p2;
        } else {
          p3 = secondScale.getRangeValue(secondNode.nodeCovered);
          p4 = secondScale.getRangeValue(secondNode.nodeCovered += (link[to] || 0));
        }
        link.points.push(p1, p2, p3, p4);
      }
    });

    // Parse link cosmetics
    linksOrder.forEach((key) => {
      let link = links[key],
        dominantNode = link.dominantNode;
      link.angles = link.points.slice(0).sort(comparator);

      if (dominantNode.length > 1 && nodes[dominantNode[0]].rawColor !== nodes[dominantNode[1]].rawColor) {
        const linkArray = nodes[dominantNode[0]].index > nodes[dominantNode[1]].index
          ? [nodes[dominantNode[1]], nodes[dominantNode[0]]]
          : [nodes[dominantNode[0]], nodes[dominantNode[1]]];
        link.rawColor = [linkArray[0].rawColor, linkArray[1].rawColor];
      } else {
        link.normalState.fill = convertColor(nodes[dominantNode[0]].rawColor, link.alpha);
        link.normalState.stroke = convertColor(nodes[dominantNode[0]].rawColor, link.borderAlpha);
        link.focussedState.fill = convertColor(nodes[dominantNode[0]].rawColor, link.hoverAlpha);
        link.focussedState.stroke = link.focussedState.fill;
        link.unfocussedState.fill = convertColor(nodes[dominantNode[0]].rawColor, link.unfocussedAlpha);
        link.unfocussedState.stroke = link.unfocussedState.fill;
      }
    });
  }

  setDimension (dim) {
    const manager = this,
      config = manager.config;

    Object.assign(config, dim);

    manager.getChildren('node') && manager.getChildren('node').forEach(node =>
      !node.getState('removed') && node.setDimension({
        outerRadius: config.nodeOuterRadius,
        innerRadius: config.nodeInnerRadius
      })
    );
    manager.getChildren('ribbon') && manager.getChildren('ribbon').forEach(link =>
      !link.getState('removed') && link.setDimension({
        radius: config.ribbonRadius
      })
    );
  }

  _manageSpace (spaceArguments) {
    const manager = this,
      widths = manager.getChildren('node').map(node => {
        return node._manageSpace(spaceArguments);
      }),
      maxWidth = Math.max(...widths);
    return {
      width: maxWidth,
      height: maxWidth
    };
  }

  _createContainer () {
    const manager = this,
      config = manager.config;
    manager.addGraphicalElement({
      el: 'group',
      container: {
        id: 'plotGroup',
        isParent: true
      },
      component: manager,
      label: 'link-container',
      id: 'link-container',
      attr: {
        name: 'link-container',
        transform: config.translate
      }
    });

    manager.addGraphicalElement({
      el: 'group',
      container: {
        id: 'plotGroup',
        isParent: true
      },
      component: manager,
      label: 'node-container',
      id: 'node-container',
      attr: {
        name: 'node-container',
        transform: config.translate,
        ...config.datalabelStyle
      }
    });
  }

  draw () {
    this._createContainer();
  }

  getName () {
    return 'node-link-manager';
  }

  getType () {
    return 'node-link-manager';
  }

  _addLegend () {
    let manager = this,
      config = manager.config,
      { nodes, nodesOrder } = config,
      legend = manager.getFromEnv('legend'),
      legendItem,
      legendItemId,
      legendItemMap = config.legendItemMap || (config.legendItemMap = {}),
      curLegendItems = new Set();

    Object.keys(legendItemMap).forEach(key => curLegendItems.add(legendItemMap[key]));
    nodesOrder.forEach(label => {
      let node = nodes[label];
      legendItemId = legendItemMap[label];
      legendItem = legend.getItem(legendItemId);
      if (!legendItem) {
        legendItemId = legend.createItem();
        legendItem = legend.getItem(legendItemId);
        legendItemMap[label] = legendItemId;
        manager.addExtEventListener('fc-click', function () {
          if (manager.config.interactiveLegend) {
            let clickedNode = manager.config.nodes[label],
              item = legend.getItem(legendItemMap[label]);
            manager.nodeClicked(clickedNode.label, item);
          }
        }, legendItem);
        manager.addExtEventListener('fc-mouseover', function () {
          if (manager.config.interactiveLegend) {
            manager.nodeHoverIn(label);
          }
        }, legendItem);
        manager.addExtEventListener('fc-mouseout', function () {
          if (manager.config.interactiveLegend) {
            manager.nodeHoverOut(label);
          }
        }, legendItem);
      } else {
        legendItem.removeLegendState('hidden');
        curLegendItems['delete'](legendItemId);
      }

      legendItem.configure({
        enabled: true,
        label: label
      });

      legendItem.setStateCosmetics('default', {
        symbol: {
          rawFillColor: node.color,
          rawStrokeColor: node.color,
          fill: node.color,
          stroke: node.color
        }
      });
    });

    curLegendItems.forEach(item => {
      legend.disposeItem(item);
    });
  }

  nodeClicked (clickedNodeLabel, legendItem) {
    if (this.config.enableToggle) {
      let config = this.config,
        { nodes, nodesOrder, links } = config,
        clickedNode = nodes[clickedNodeLabel],
        linkedLinks = clickedNode.linkedLinks,
        isActive = clickedNode.active;

      if (clickedNode.active) {
        legendItem && legendItem.setLegendState('hidden');
      } else {
        legendItem && legendItem.removeLegendState('hidden');
      }

      clickedNode.active = !isActive;
      linkedLinks.forEach(key => {
        let link = links[key],
          from = config.isPost ? link.linkedNodes[1] : link.linkedNodes[0],
          to = config.isPost ? link.linkedNodes[0] : link.linkedNodes[1],
          fromNode = nodes[from],
          toNode = nodes[to],
          isVisisble = link.visible;
        link.visible = fromNode.active && toNode.active;
        if (link.visible !== isVisisble) {
          if (link.visible) {
            fromNode.total = toPrecision(fromNode.total + (link[from] || 0), 4);
            if (to !== from) {
              toNode.total = toPrecision(toNode.total + (link[to] || 0), 4);
            }
          } else {
            fromNode.total = toPrecision(fromNode.total - (link[from] || 0), 4);
            if (to !== from) {
              toNode.total = toPrecision(toNode.total - (link[to] || 0), 4);
            }
          }
        }
      });
      nodesOrder.forEach(label => (nodes[label].nodeCovered = 0));
      if (isActive) {
        this.nodeHoverOut(clickedNodeLabel, false);
      } else {
        this.nodeHoverIn(clickedNodeLabel, false);
      }
      this.setData({}, true);
      this.setDimension();
    }
  }

  linkHoverIn (linkKey, registerDraw = true) {
    if (this.config.highlightEffect) {
      let manager = this,
        config = manager.config,
        { links, nodes, nodesOrder, linksOrder } = config,
        hoveredLink = links[linkKey],
        hoveredNodes = hoveredLink.linkedNodes;

      nodesOrder.forEach(label => {
        let node = nodes[label],
          child = manager.getChild(label);
        if (!child || child.getState('removed')) {
          return;
        }
        if (hoveredNodes.includes(label)) {
          node.hovered = true;
          node.unfocussed = false;
        } else {
          node.hovered = false;
          node.unfocussed = true;
        }
        registerDraw && child.setData({
          hovered: node.hovered,
          unfocussed: node.unfocussed
        }, true);
      });
      linksOrder.forEach(key => {
        let link = links[key],
          child = manager.getChild(key);
        if (!child || child.getState('removed')) {
          return;
        }
        if (key === linkKey) {
          link.hovered = true;
          link.unfocussed = false;
        } else {
          link.hovered = false;
          link.unfocussed = true;
        }
        registerDraw && child.setData({
          hovered: link.hovered,
          unfocussed: link.unfocussed
        }, true);
      });
      registerDraw && this.setDimension();
    }
  }
  resetEffect (registerDraw = true) {
    let manager = this,
      config = manager.config,
      { links, nodes, nodesOrder, linksOrder } = config;

    nodesOrder.forEach(label => {
      let node = nodes[label],
        child = manager.getChild(label);
      if (!child || child.getState('removed')) {
        return;
      }
      node.hovered = false;
      node.unfocussed = false;
      registerDraw && child.setData({
        hovered: node.hovered,
        unfocussed: node.unfocussed
      }, true);
    });
    linksOrder.forEach(key => {
      let link = links[key],
        child = manager.getChild(key);
      if (!child || child.getState('removed')) {
        return;
      }
      link.hovered = false;
      link.unfocussed = false;
      registerDraw && child.setData({
        hovered: link.hovered,
        unfocussed: link.unfocussed
      }, true);
    });
    this.setDimension();
  }
  linkHoverOut (linkKey, registerDraw = true) {
    if (this.config.highlightEffect) {
      this.resetEffect(registerDraw);
    }
  }
  nodeHoverIn (labelKey, registerDraw = true) {
    if (this.config.highlightEffect) {
      let manager = this,
        config = manager.config,
        { links, nodes, nodesOrder, linksOrder } = config,
        hoveredNode = nodes[labelKey],
        hoveredLinks = hoveredNode.linkedLinks,
        affectedNodes = hoveredLinks.map(key => links[key].linkedNodes[0] === labelKey
          ? links[key].linkedNodes[1]
          : links[key].linkedNodes[0]);
      affectedNodes.push(labelKey);

      nodesOrder.forEach(label => {
        let node = nodes[label],
          child = manager.getChild(label);
        if (!child || child.getState('removed')) {
          return;
        }
        if (affectedNodes.includes(label)) {
          node.hovered = true;
          node.unfocussed = false;
        } else {
          node.hovered = false;
          node.unfocussed = true;
        }
        registerDraw && hoveredNode.active && child.setData({
          hovered: node.hovered,
          unfocussed: node.unfocussed
        }, true);
      });
      linksOrder.forEach(key => {
        let link = links[key],
          child = manager.getChild(key);
        if (!child || child.getState('removed')) {
          return;
        }
        if (hoveredLinks.includes(key)) {
          link.hovered = true;
          link.unfocussed = false;
        } else {
          link.hovered = false;
          link.unfocussed = true;
        }
        registerDraw && hoveredNode.active && child.setData({
          hovered: link.hovered,
          unfocussed: link.unfocussed
        }, true);
      });
      registerDraw && hoveredNode.active && this.setDimension();
    }
  }
  nodeHoverOut (labelKey, registerDraw = true) {
    if (this.config.highlightEffect) {
      this.resetEffect(registerDraw);
    }
  }
}

export default NodeLinkManager;
